// UI spritesheet metadata (`public/sprites/ui/ui.png`). Grid of 16×16
// cells. The format follows the Pixi convention (frames record + meta), so
// it can be used directly via `new PIXI.Spritesheet()` later if part of the
// menu moves to Pixi rendering. For now, the main consumer is
// `NineSliceFrame`, which renders in the DOM via `border-image`.

export const UI_ATLAS_IMAGE = '/sprites/ui/ui.png';

/** Size of one cell in source pixels. */
export const UI_ATLAS_CELL = 16;

/** Total width of `ui.png` (1216 = 76 × 16). */
export const UI_ATLAS_WIDTH = 1216;

/** Total height of `ui.png` (448 = 28 × 16). */
export const UI_ATLAS_HEIGHT = 448;

/** Atlas borders (4×4-cell 9-slice frames). */
export const UI_BORDERS_IMAGE = '/sprites/ui/borders.png';
export const UI_BORDERS_WIDTH = 320;
export const UI_BORDERS_HEIGHT = 256;

interface PixiFrame {
  frame: { x: number; y: number; w: number; h: number };
}

/** Builds a frame entry from cell coordinates. */
const cell = (col: number, row: number): PixiFrame => ({
  frame: {
    x: col * UI_ATLAS_CELL,
    y: row * UI_ATLAS_CELL,
    w: UI_ATLAS_CELL,
    h: UI_ATLAS_CELL,
  },
});

/**
 * 6 cells forming a tiled trackbar (cells cols 5–10 × row 16 in `ui.png`).
 * Composed left to right: decorative left arrow, filled start, filled mid
 * (tileable), empty mid (tileable), empty end, decorative right arrow. The
 * final trackbar = `[arrow-left] [filled-start] [filled-mid × N]
 * [empty-mid × M] [empty-end] [arrow-right]`.
 */
export const UI_BAR_FRAMES: Record<string, PixiFrame> = {
  'bar-arrow-left': cell(5, 16),
  'bar-filled-start': cell(6, 16),
  'bar-filled-mid': cell(7, 16),
  'bar-empty-mid': cell(8, 16),
  'bar-empty-end': cell(9, 16),
  'bar-arrow-right': cell(10, 16),
  /** "track" sprite (filled/empty junction cursor). */
  'bar-track': cell(6, 15),
};

/**
 * 9 cells forming the main panel frame (cells cols 9–11 × rows 1–3 in
 * `ui.png`). Standard 9-slice naming: tl/t/tr/l/c/r/bl/b/br.
 */
export const UI_PANEL_FRAME_FRAMES: Record<string, PixiFrame> = {
  'panel-frame-tl': cell(9, 1),
  'panel-frame-t': cell(10, 1),
  'panel-frame-tr': cell(11, 1),
  'panel-frame-l': cell(9, 2),
  'panel-frame-c': cell(10, 2),
  'panel-frame-r': cell(11, 2),
  'panel-frame-bl': cell(9, 3),
  'panel-frame-b': cell(10, 3),
  'panel-frame-br': cell(11, 3),
};

/**
 * Complete Pixi-format spritesheet for the panel frame. `image` points to
 * the source file; `meta.size` the native dimensions needed by
 * `PIXI.Spritesheet`; frames are keyed by their logical name.
 */
export const UI_PANEL_FRAME_SHEET = {
  frames: UI_PANEL_FRAME_FRAMES,
  meta: {
    image: UI_ATLAS_IMAGE,
    size: { w: UI_ATLAS_WIDTH, h: UI_ATLAS_HEIGHT },
    scale: '1',
  },
};

/**
 * Contiguous region (48×48 px) grouping the 9 panel-frame cells of
 * `ui.png`. Kept for reference/back-compat; active rendering now goes
 * through `UI_BORDERS_PANEL_REGION` (on `borders.png`).
 */
export const UI_PANEL_FRAME_REGION = {
  image: UI_ATLAS_IMAGE,
  x: 9 * UI_ATLAS_CELL,
  y: 1 * UI_ATLAS_CELL,
  size: 3 * UI_ATLAS_CELL,
  cell: UI_ATLAS_CELL,
};

/**
 * 9-slice frame from `borders.png`. 4×4-cell region starting at cell
 * (8, 8) — i.e. pixel (128, 128), size 64×64 px. The two central cells on
 * each axis are identical in the source, so `border-image-slice: 16`
 * treats them as a single stretched band (CSS stretches the inner 32×32
 * block to the container size).
 */
export const UI_BORDERS_PANEL_REGION = {
  image: UI_BORDERS_IMAGE,
  x: 8 * UI_ATLAS_CELL,
  y: 8 * UI_ATLAS_CELL,
  size: 4 * UI_ATLAS_CELL,
  cell: UI_ATLAS_CELL,
};

/**
 * 9-slice frame dedicated to the forge screen. 3×3-cell region starting at
 * (13, 8) in `interface.png` — pixel (208, 128), size 48×48 px.
 */
export const UI_FORGE_FRAME_IMAGE = '/sprites/ui/interface.png';
export const UI_FORGE_FRAME_REGION = {
  image: UI_FORGE_FRAME_IMAGE,
  x: 13 * UI_ATLAS_CELL,
  y: 8 * UI_ATLAS_CELL,
  size: 3 * UI_ATLAS_CELL,
  cell: UI_ATLAS_CELL,
};

/** 9-slice frame for the forge detail panel (left). `ui.png` (26..28, 1..3). */
export const UI_FORGE_DETAIL_FRAME_REGION = {
  image: UI_ATLAS_IMAGE,
  x: 26 * UI_ATLAS_CELL,
  y: 1 * UI_ATLAS_CELL,
  size: 3 * UI_ATLAS_CELL,
  cell: UI_ATLAS_CELL,
};

/** 9-slice frame for the forge item grid (right). `ui.png` (18..20, 1..3). */
export const UI_FORGE_GRID_FRAME_REGION = {
  image: UI_ATLAS_IMAGE,
  x: 18 * UI_ATLAS_CELL,
  y: 1 * UI_ATLAS_CELL,
  size: 3 * UI_ATLAS_CELL,
  cell: UI_ATLAS_CELL,
};

/**
 * 9-slice frame used for `ItemCard`s. 4×4-cell region starting at (4, 12)
 * in `borders.png`. Like `UI_BORDERS_PANEL_REGION`, the two central cells
 * on each axis are identical in the source and treated as a single band by
 * `border-image-slice: 16`.
 */
export const UI_ITEM_CARD_FRAME_REGION = {
  image: UI_BORDERS_IMAGE,
  x: 16 * UI_ATLAS_CELL,
  y: 8 * UI_ATLAS_CELL,
  size: 4 * UI_ATLAS_CELL,
  cell: UI_ATLAS_CELL,
};

/**
 * 9-slice frame for the loadout book. Rectangular source `book.png`:
 * 10×6-cell region starting at cell (1, 2), i.e. pixel (16, 32), size
 * 160×96 px. Represents a book open on two pages (2 × 5 cells wide).
 * 1-cell corners, middle stretched horizontally and vertically by CSS
 * border-image.
 */
export const UI_BOOK_IMAGE = '/sprites/ui/book.png';
export const UI_BOOK_FRAME_REGION = {
  image: UI_BOOK_IMAGE,
  x: 1 * UI_ATLAS_CELL,
  y: 2 * UI_ATLAS_CELL,
  size: 10 * UI_ATLAS_CELL,
  height: 6 * UI_ATLAS_CELL,
  cell: UI_ATLAS_CELL,
};

/**
 * 9-slice frame used as background + border for each inventory / chest /
 * equipment-grid slot. 3×3-cell region starting at (20, 14) in `ui.png` —
 * pixel (320, 224), size 48×48 px. Consumed by `ItemSlot`.
 */
export const UI_ITEM_SLOT_FRAME_REGION = {
  image: UI_ATLAS_IMAGE,
  x: 20 * UI_ATLAS_CELL,
  y: 14 * UI_ATLAS_CELL,
  size: 3 * UI_ATLAS_CELL,
  cell: UI_ATLAS_CELL,
};

/**
 * Flat (non-9-slice) atlas region: a sub-rect stretched to fill its element.
 * Rendered by `AtlasImage`.
 */
export interface UiFlatRegion {
  image: string;
  x: number;
  y: number;
  w: number;
  h: number;
  atlasWidth: number;
  atlasHeight: number;
}
